const $$splitComponentImporter = () => import('/app/apps/web/src/routes/_app/company/$id.tsx?tsr-split=component');
import { lazyRouteComponent } from '@tanstack/react-router';
const $$splitLoaderImporter = () => import('/app/apps/web/src/routes/_app/company/$id.tsx?tsr-split=loader');
import { lazyFn } from '@tanstack/react-router';
import { createFileRoute } from "@tanstack/react-router";
import { createTranslator } from "use-intl";
import { toAbsoluteUrl } from "@/lib/absolute-url";
import { resolveCatalogEntitySeo } from "@/lib/catalog-seo-policy";
import { COMPANY_DISCOGRAPHY_URL_CONFIG, pickNumberArray, pickReleaseSearchFilter } from "@/lib/faceted-search-url";
import { jsonLdScripts } from "@/lib/jsonLdScripts";
import { withSiteTitle } from "@/lib/page-title";
import { pickString, pickStringArray } from "@/lib/search-params";
import { buildSeoMetaTags } from "@/lib/seo-meta";
import { getRootLoaderData } from "@/types/intl";
interface CompanySearch {
  q?: string;
  type?: string;
  sort?: string;
  format?: string;
  genre?: string[];
  style?: string[];
  country?: string;
  label?: string[];
  company?: string[];
  year?: number[];
  filter?: ReleaseSearchFilter;
}
export const Route = createFileRoute("/_app/company/$id")({
  validateSearch: (raw: Record<string, unknown>): CompanySearch => ({
    q: pickString(raw.q),
    type: pickString(raw.type),
    sort: pickString(raw.sort),
    format: pickString(raw.format),
    genre: pickStringArray(raw.genre),
    style: pickStringArray(raw.style),
    country: pickString(raw.country),
    label: pickStringArray(raw.label),
    company: pickStringArray(raw.company),
    year: pickNumberArray(raw.year),
    filter: pickReleaseSearchFilter(raw.filter, COMPANY_DISCOGRAPHY_URL_CONFIG)
  }),
  // Keep high-frequency text search out of loaderDeps so each keystroke does
  // not create a new route match. The loader still reads `location.search.q`
  // on route entry to SSR-prefetch direct `/company/:id?q=...` loads.
  loaderDeps: ({
    search
  }) => ({
    type: search.type,
    sort: search.sort,
    format: search.format,
    genre: search.genre,
    style: search.style,
    country: search.country,
    label: search.label,
    company: search.company,
    year: search.year,
    filter: search.filter
  }),
  loader: lazyFn($$splitLoaderImporter, 'loader'),
  head: async ({
    match,
    loaderData,
    params
  }) => {
    const {
      locale,
      messages
    } = getRootLoaderData(match);
    const tMeta = createTranslator({
      locale,
      messages,
      namespace: "globals.metadata"
    });
    const tSeo = createTranslator({
      locale,
      messages,
      namespace: "globals.seo"
    });
    const company = loaderData?.company;
    if (!company) {
      return {
        meta: [{
          title: withSiteTitle(tMeta("company"))
        }]
      };
    }
    const {
      buildCompanySeoDescription,
      buildCompanySeoTitle,
      buildDefaultCommerceSeoFormat,
      formatAvailableTypeLabels,
      formatSeoCountryList
    } = await import("@/lib/seo");
    const {
      buildCatalogPageJsonLd,
      buildCompanyJsonLd
    } = await import("@/utils/jsonld");
    const entitySeo = resolveCatalogEntitySeo({
      policy: company.seo,
      currentEntityType: "company",
      currentCatalogId: company.id,
      locale
    });
    const typeLabels = formatAvailableTypeLabels(loaderData.discographyTypeCounts, tSeo, locale);
    const formats = buildDefaultCommerceSeoFormat({
      tSeo,
      locale
    });
    const title = buildCompanySeoTitle({
      name: company.name,
      formats,
      tSeo
    });
    const description = buildCompanySeoDescription({
      name: company.name,
      typeLabels,
      formats,
      countries: formatSeoCountryList(company.countryCodes, locale),
      parentName: company.parentName,
      tSeo,
      locale
    });
    const url = entitySeo.canonicalUrl;
    const imageUrl = toAbsoluteUrl(company.imageUrl) ?? undefined;
    const jsonLd = buildCatalogPageJsonLd({
      entity: company,
      emitStructuredData: entitySeo.emitStructuredData,
      locale,
      buildEntityJsonLd: company => buildCompanyJsonLd(company, description),
      buildBreadcrumbItems: company => [{
        name: createTranslator({
          locale,
          messages,
          namespace: "label"
        })("breadcrumbSearch"),
        url: "/search"
      }, {
        name: company.name,
        url: `/company/${params.id}`
      }]
    });
    return {
      meta: buildSeoMetaTags({
        title,
        description,
        canonicalUrl: url,
        imageUrl
      }, {
        robots: entitySeo.robots,
        openGraph: {
          type: "website"
        },
        twitter: {
          includeImage: false
        }
      }),
      links: entitySeo.links,
      scripts: jsonLdScripts(jsonLd?.entity, jsonLd?.breadcrumb)
    };
  },
  component: lazyRouteComponent($$splitComponentImporter, 'component')
});